
import * as pdfjsLib from 'pdfjs-dist';

// ✅ GLOBAL FLAG: Rastreia inicialização entre componentes
declare global {
  interface Window {
    __pdfJsInitialized?: boolean;
    __SW_IFRAME_LOADED?: boolean;
  }
}

// ✅ PROMISE-LOCK: Controle robusto para inicialização única
let isWorkerInitialized = false;
let initializationInProgress = false;
let initializationPromise: Promise<boolean> | null = null;
let initializationAttempts = 0;
const MAX_INITIALIZATION_ATTEMPTS = 3;

/**
 * Checks if PDF.js worker is available
 */
export const isPdfWorkerAvailable = (): boolean => {
  return isWorkerInitialized && (typeof window !== 'undefined' ? window.__pdfJsInitialized === true : false);
};

/**
 * ✅ FUNÇÃO CENTRALIZADA: Inicialização única sem fake-worker
 */
export const initializePdfJs = async (): Promise<boolean> => {
  // ✅ GUARD 1: Verifica flag global
  if (typeof window !== 'undefined' && window.__pdfJsInitialized) {
    console.log('PDF.js: Already initialized (global flag)');
    return true;
  }
  
  // ✅ GUARD 2: Verifica estado interno
  if (isWorkerInitialized) {
    console.log('PDF.js: Already initialized (internal flag)');
    return true;
  }
  
  // ✅ GUARD 3: Se há inicialização em progresso, aguarda a mesma Promise
  if (initializationInProgress && initializationPromise) {
    console.log('PDF.js: Awaiting initialization in progress...');
    return initializationPromise;
  }
  
  // ✅ GUARD 4: Limita tentativas de inicialização
  if (initializationAttempts >= MAX_INITIALIZATION_ATTEMPTS) {
    console.warn('PDF.js: Maximum initialization attempts reached');
    return false;
  }
  
  // ✅ PROMISE-LOCK: Marca como em progresso e cria Promise única
  initializationInProgress = true;
  initializationAttempts++;
  
  console.log(`PDF.js: Starting initialization attempt ${initializationAttempts}/${MAX_INITIALIZATION_ATTEMPTS}`);
  
  initializationPromise = performSingleInitialization();
  
  try {
    const result = await initializationPromise;
    if (result) {
      isWorkerInitialized = true;
      if (typeof window !== 'undefined') {
        window.__pdfJsInitialized = true;
      }
      console.log('✅ PDF.js: Initialization completed successfully');
    }
    return result;
  } catch (error) {
    console.error('❌ PDF.js: Initialization error:', error);
    return false;
  } finally {
    initializationInProgress = false;
    initializationPromise = null;
  }
};

// 🔧 CORREÇÃO DEFINITIVA: Flag de módulo para evitar dupla inicialização
let PDF_INIT_LOCK = false;

/**
 * ✅ INICIALIZAÇÃO REAL: Usando Worker direto (sem fake-worker/iframe)
 */
const performSingleInitialization = async (): Promise<boolean> => {
  try {
    // 🔧 GUARD TRIPLO: flag local + worker configurado + window flags
    if (PDF_INIT_LOCK || pdfjsLib.GlobalWorkerOptions.workerSrc) {
      console.log('PDF.js: Already initialized via module flag or worker, skipping');
      return true;
    }
    
    if (typeof window !== 'undefined') {
      if (window.__SW_IFRAME_LOADED || window.__pdfJsInitialized) {
        console.log('PDF.js: SW/iframe already loaded, skipping');
        return true;
      }
      // Marcar flags para evitar dupla inicialização
      window.__SW_IFRAME_LOADED = true;
      window.__pdfJsInitialized = true;
    }
    
    PDF_INIT_LOCK = true;
    
    console.log('PDF.js: Performing single initialization without fake-worker...');
    
    // ✅ PROTEÇÃO ADICIONAL: Só define worker se ainda não foi definido
    if (pdfjsLib.GlobalWorkerOptions.workerSrc || pdfjsLib.GlobalWorkerOptions.workerPort) {
      console.log('PDF.js: Worker already configured, skipping setup');
      return true;
    }
    
    if (typeof window !== 'undefined' && 'Worker' in window) {
      try {
        // ✅ SOLUÇÃO DEFINITIVA: Worker via CDN compatível - sem fake-worker/iframe
        const pdfVersion = pdfjsLib.version;
        const workerUrl = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfVersion}/build/pdf.worker.min.js`;
        
        // Verificar se o worker está acessível
        const response = await fetch(workerUrl, { method: 'HEAD' });
        if (response.ok) {
          pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
          console.log('✅ PDF.js: CDN Worker configured successfully - NO sw_iframe.html');
          return true;
        } else {
          throw new Error('Worker not accessible via CDN');
        }
      } catch (workerError) {
        console.warn('PDF.js: CDN Worker failed, using no worker mode:', workerError);
        // Fallback: sem worker (hotfix mode) - GARANTIR workerPort = null
        (pdfjsLib as any).GlobalWorkerOptions.workerPort = null;
        pdfjsLib.GlobalWorkerOptions.workerSrc = '';
        console.log('✅ PDF.js: Using no-worker mode with workerPort=null - NO sw_iframe.html');
        return true;
      }
    } else {
      // Server-side ou sem suporte a Worker
      pdfjsLib.GlobalWorkerOptions.workerSrc = '';
      console.log('✅ PDF.js: Using without worker (server-side or no Worker support)');
      return true;
    }
  } catch (error) {
    console.error('❌ PDF.js: Initialization error:', error);
    return false;
  }
};

/**
 * Gets a PDF document instance from a file
 */
export const getPdfDocument = async (file: File, onProgress?: (progress: number) => void): Promise<pdfjsLib.PDFDocumentProxy | null> => {
  try {
    if (!isWorkerInitialized) {
      await initializePdfJs();
    }
    
    // Create a URL for the file
    const fileURL = URL.createObjectURL(file);
    
    // Load the PDF document with advanced options for better extraction
    const loadingTask = pdfjsLib.getDocument({
      url: fileURL,
      // worker: null removido - agora usando Module Worker
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
      standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/',
      disableAutoFetch: false,
      disableStream: false,
      disableRange: false
    });
    
    // Add progress listener if provided
    if (onProgress) {
      loadingTask.onProgress = (data) => {
        const progress = data.loaded / (data.total || data.loaded * 2) * 50; // First 50% is loading
        onProgress(Math.min(50, Math.max(0, progress)));
      };
    }
    
    const pdf = await loadingTask.promise;
    
    // Signal we're halfway done (loading complete, extraction beginning)
    if (onProgress) onProgress(50);
    
    return pdf;
  } catch (error) {
    console.error('Error loading PDF document:', error);
    return null;
  }
};

// Export all functions and PDF.js library
export { pdfjsLib };
